import { BookOpen, ListChecks } from 'lucide-react'

/* La colonne « UP-PROJETS » des listes : deux liens, deux espaces.
 *
 * Arbitré en réunion le 27/08/2026, puis précisé le 28. Le bouton « Créer et
 * écrire » a été remplacé par deux icônes, parce que le geste courant n'est pas
 * d'écrire : c'est d'aller voir. Frédéric Creuwels l'a dit en séance — on peut
 * simplement vouloir relire une note existante.
 *
 *   📖  le livre  → l'espace documentaire de l'entreprise
 *   ☑   les tâches → ses projets, et le suivi des actions
 *
 * **L'icône porte l'information autant que l'action** : sa couleur dit, sans
 * ouvrir la fiche, si l'entreprise a des projets et si elle a un espace. C'est
 * la raison d'être des deux états, pas un ornement.
 *
 * Le lien ne transporte que des identifiants, jamais un nom ni une adresse :
 * les noms sont relus depuis UP-CRM à l'arrivée, ce qui garantit qu'un contact
 * renommé ici arrive là-bas avec son nom d'aujourd'hui.
 */

const URL_UP_PROJETS = (import.meta as any).env.VITE_UPPROJETS_URL || ''

export const SUIVI_DOCUMENTAIRE = 'documentaire'
export const SUIVI_ACTIONS = 'actions'

interface Props {
  /** L'identifiant UP-CRM de l'entreprise. Sans lui, il n'y a rien à ouvrir. */
  companyId?: number | null
  /* Les valeurs cochées du champ « Suivi dans UP-PROJETS ».
     `undefined` veut dire INCONNU — pas « rien ». La liste des contacts ne
     charge pas les champs personnalisés des entreprises : on n'y affiche donc
     pas d'état plutôt que d'en affirmer un faux. */
  suivi?: string[] | null
  /** Le nom, pour le dire dans la confirmation et les infobulles. */
  nom?: string
}

export function UpProjetsCell({ companyId, suivi, nom }: Props) {
  if (!companyId) return <span className="text-xs text-slate-400">—</span>

  if (!URL_UP_PROJETS) {
    return (
      <span className="text-xs text-slate-400" title="La variable VITE_UPPROJETS_URL n'est pas renseignée.">
        UP-PROJETS non configuré
      </span>
    )
  }

  const statutConnu = Array.isArray(suivi)
  const aUnEspace = statutConnu && suivi.includes(SUIVI_DOCUMENTAIRE)
  const aDesProjets = statutConnu && suivi.includes(SUIVI_ACTIONS)
  const cetteEntreprise = nom ? ` de ${nom}` : ''

  const lien = (onglet: 'documents' | 'projets') =>
    `${URL_UP_PROJETS.replace(/\/+$/, '')}/depuis-crm?entreprise=${companyId}&onglet=${onglet}`

  const ouvrirLEspace = (e: React.MouseEvent) => {
    e.stopPropagation()
    /* L'espace n'existe pas encore : on le DIT avant de le créer. Un espace qui
       apparaît sans prévenir dans l'autre application est une mauvaise
       surprise — et le statut inconnu vaut ici « pas sûr », donc on demande
       aussi. */
    if (!aUnEspace && !window.confirm(
      `L'espace documentaire${cetteEntreprise} n'existe pas encore dans UP-PROJETS.\n\n`
      + 'Continuer va le créer, et cocher la case « Espace documentaire » sur cette fiche.',
    )) return
    window.open(lien('documents'), '_blank', 'noopener')
  }

  const pastille = (actif: boolean) =>
    `inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors ${
      actif
        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
        : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
    }`

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className={pastille(aUnEspace)}
        title={aUnEspace
          ? `Ouvrir l'espace documentaire${cetteEntreprise} dans UP-PROJETS`
          : `Créer l'espace documentaire${cetteEntreprise} dans UP-PROJETS, et l'ouvrir`}
        onClick={ouvrirLEspace}
      >
        <BookOpen className="w-[18px] h-[18px]" />
      </button>

      {/* Sans projet, l'icône est ÉTEINTE et ne mène nulle part : il n'y a rien
          à ouvrir, et un lien qui aboutit à une liste vide vaut moins qu'une
          icône qui dit d'un coup d'œil qu'il n'y a rien. */}
      {aDesProjets ? (
        <button
          type="button"
          className={pastille(true)}
          title={`Ouvrir le suivi des actions${cetteEntreprise} dans UP-PROJETS`}
          onClick={(e) => { e.stopPropagation(); window.open(lien('projets'), '_blank', 'noopener') }}
        >
          <ListChecks className="w-[18px] h-[18px]" />
        </button>
      ) : (
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-slate-50 text-slate-300 cursor-default"
          title={statutConnu
            ? `Aucun projet${cetteEntreprise} dans UP-PROJETS`
            : 'Suivi des actions — état inconnu depuis cette liste'}
        >
          <ListChecks className="w-[18px] h-[18px]" />
        </span>
      )}
    </div>
  )
}

/* Sur la fiche d'un contact (01/10/2026) : un lien vers les projets de son
 * entreprise dans UP-PROJETS. « La personne est dans un projet » veut dire ici
 * « son entreprise a des projets » : c'est à ce niveau que les deux
 * applications sont reliées. UP-PROJETS n'offre pas de lecture par machine, on
 * ne liste donc pas les projets : on ouvre la même page que l'icône des listes.
 *
 * Le lien n'est proposé que si la case « Suivi des actions » de l'entreprise
 * est cochée — sinon il mènerait à une page vide. */
export function LienProjetsDeLEntreprise({ companyId, suivi }: { companyId?: number | null; suivi: string[] | null }) {
  if (!companyId || !URL_UP_PROJETS) return null
  if (suivi === null) return <p className="text-xs text-slate-400">Lecture du suivi dans UP-PROJETS…</p>
  if (!suivi.includes(SUIVI_ACTIONS)) {
    return <p className="text-xs text-slate-500">Son entreprise n'a pas de projet suivi dans UP-PROJETS.</p>
  }
  return (
    <a
      href={`${URL_UP_PROJETS.replace(/\/+$/, '')}/depuis-crm?entreprise=${companyId}&onglet=projets`}
      target="_blank"
      rel="noopener"
      className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-700 hover:underline"
    >
      <ListChecks className="w-4 h-4" />
      Voir les projets de l'entreprise dans UP-PROJETS
    </a>
  )
}
